import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { Drawer } from 'vaul';

import { RecentSymptomItem } from '@/entities/diagnosis';
import { Button } from 'doctornow-design-system';

interface Props {
  open: boolean;
  items: RecentSymptomItem[];
  onClose: () => void;
  onSubmit: (selectedItem: RecentSymptomItem) => void;
}

const SymptomBottomSheet = ({ open, items, onClose, onSubmit }: Props) => {
  const [selectedItem, setSelectedItem] = useState<RecentSymptomItem | null>(null);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedItem(null);
      onClose();
    }
  };

  const handleSubmit = () => {
    if (selectedItem) {
      onSubmit(selectedItem);
      setSelectedItem(null);
    }
  };

  return (
    <Drawer.Root modal open={open} onOpenChange={handleOpenChange} dismissible>
      <Drawer.Portal>
        <StyledOverlay />
        <Content>
          <Drawer.Handle className='mx-auto mb-[16px] h-[4px] w-[48px] flex-shrink-0 rounded-full bg-gray-300' />
          <Title>증상 불러오기</Title>
          <ListWrapper>
            {items.map((item) => (
              <RadioItem key={item.id} selected={selectedItem?.id === item.id} onClick={() => setSelectedItem(item)}>
                <span>{item.symptom}</span>
              </RadioItem>
            ))}
          </ListWrapper>
          <ButtonWrapper>
            <Button
              buttonStyle='contained'
              buttonColor='primary'
              size='xl'
              disabled={!selectedItem}
              className='w-full'
              onClick={handleSubmit}
            >
              <span>불러오기</span>
            </Button>
          </ButtonWrapper>
        </Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default SymptomBottomSheet;

const StyledOverlay = styled(Drawer.Overlay)`
  position: fixed;
  inset: 0;
  z-index: 260;
  margin: 0 auto;
  max-width: 768px;
  background-color: rgba(0, 0, 0, 0.4);
`;

const Content = styled(Drawer.Content)`
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 261;
  margin: 0 auto;
  max-width: 768px;
  background-color: ${({ theme }) => theme.color.white};
  border-radius: 10px 10px 0 0;
  padding: 8px 20px 20px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0px -2px 8px 0px rgba(0, 0, 0, 0.16);

  &:focus-visible {
    outline: none;
  }
`;

const Title = styled.h2`
  ${({ theme }) => css`
    ${theme.typography.UI22SB}
    color: ${theme.color.gray900};
    margin-bottom: 20px;
    padding: 0 4px;
  `}
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  max-height: 400px;
  margin-bottom: 24px;
  flex: 1;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const RadioItem = styled.div<{ selected: boolean }>`
  position: relative;
  border-radius: 8px;
  padding: 16px 16px 16px 54px;
  cursor: pointer;
  transition: all 0.2s ease;

  ${({ theme, selected }) => css`
    ${theme.typography.UI15M};
    background-color: ${theme.color.gray20};
    color: ${theme.color.gray700};

    & > span {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &::before {
      position: absolute;
      content: '';
      top: 50%;
      left: 16px;
      transform: translateY(-50%);
      border-radius: 50%;
      width: 22px;
      height: 22px;
      background-color: ${theme.color.white};
      border: ${selected ? `5px solid ${theme.color.primary500}` : `2px solid ${theme.color.gray100}`};
      transition: border 0.2s ease;
    }

    &:hover {
      background-color: ${theme.color.gray50};
    }
  `}
`;

const ButtonWrapper = styled.div`
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.color.gray100};
`;
